import { stripObject } from '@hgiasac/helper';
import { DocumentNode } from 'graphql';
import { assign } from 'lodash';
import { IPagingResult } from 'root/models';
import { gqlClient } from '.';
import { convertFilterParams, gqlQueryOption, IGqlQueryOption, IPaginationParams } from './utils';

export interface ICRUDGqlService<M> {
  filter(query: DocumentNode, distinctOn?: string, pagingParams?: IPaginationParams,
         orderParams?: any, options?: IGqlQueryOption): Promise<IPagingResult<M>>;
  fetchMany(query: DocumentNode, options?: IGqlQueryOption, variables?: any): Promise<M>;
  getById(query: DocumentNode, id: string): Promise<M>;
  update(query: DocumentNode, id: string, form: any): Promise<M>;
  create(query: DocumentNode, form: any): Promise<M>;
  createMany(query: DocumentNode, form: any[]): Promise<M[]>;
  deleteById(query: DocumentNode, id: string): Promise<any>;
  upsert(query: DocumentNode, params: any, form: any): Promise<M>;
  upsertMany(query: DocumentNode, params: any, form: any[]): Promise<M[]>;
}

export function CRUDGqlService<M>(prefix: string): ICRUDGqlService<M> {
  async function filter(
    query: DocumentNode,
    distinctOn: string,
    pagingParams: IPaginationParams,
    orderParams: any,
    options: IGqlQueryOption): Promise<IPagingResult<M>> {
    const { page, limit } = pagingParams,
      { fetchPolicy, filterParams } = assign(gqlQueryOption(), options);
    const params = filterParams ? filterParams : {};

    const result = await gqlClient.query({
      query,
      fetchPolicy,
      variables: stripObject({
        limit,
        orderParams,
        offset: (page - 1) * limit,
        filter: {
          ...convertFilterParams(params)
        },
        distinctOn: distinctOn ? distinctOn : null
      })
    });

    if (result.errors) {
      throw result.errors;
    }

    return {
      data: result.data[prefix],
      pagination: {
        page,
        limit,
        totalPages: Math.ceil(result
          .data[`${prefix}_aggregate`]
          .aggregate.count / limit),
        total: result
            .data[`${prefix}_aggregate`]
            .aggregate.count
      }
    };
  }

  async function fetchMany(
    query: DocumentNode,
    options?: IGqlQueryOption,
    variables?: any): Promise<M> {
    const { fetchPolicy } = assign(gqlQueryOption(), options);
    variables = variables ? variables : {};
    const result = await gqlClient.query({
      query,
      fetchPolicy,
      variables
    });

    if (result.errors) {
      throw result.errors;
    }

    return result.data[prefix];
  }

  async function getById(
    query: DocumentNode,
    id: string
  ): Promise<M> {
    const result = await gqlClient.query({
      query,
      fetchPolicy: 'network-only',
      variables: {
        id
      }
    });

    if (result.errors) {
      throw result.errors;
    }

    return result.data[`${prefix}`];
  }

  async function update(
    query: DocumentNode,
    id: string,
    form: any
  ): Promise<M> {
    const result = await gqlClient.mutate({
      mutation: query,
      variables: {
        id,
        form
      }
    });
    if (result.errors) {
      throw result.errors;
    }

    return result.data[`update_${prefix}`].returning[0];
  }
  async function deleteById(
    query: DocumentNode,
    id: string
  ): Promise<M> {
    const result = await gqlClient.mutate({
      mutation: query,
      variables: {
        id
      }
    });
    if (result.errors) {
      throw result.errors;
    }

    return result.data[`update_${prefix}`].returning[0];
  }
  async function create(
    query: DocumentNode,
    form: any
  ): Promise<M> {
    const result = await gqlClient.mutate({
      mutation: query,
      variables: {
        form
      }
    });
    if (result.errors) {
      throw result.errors;
    }

    return result.data[`insert_${prefix}`].returning[0];
  }
  async function createMany(
    query: DocumentNode,
    form: any[]
  ): Promise<M[]> {
    const result = await gqlClient.mutate({
      mutation: query,
      variables: {
        form
      }
    });
    if (result.errors) {
      throw result.errors;
    }

    return result.data[`insert_${prefix}`].returning;
  }
  async function upsert(
    query: DocumentNode,
    params: any,
    form: any
  ): Promise<M> {
    const result = await gqlClient.mutate({
      mutation: query,
      variables: {
        form,
        ...params
      }
    });
    if (result.errors) {
      throw result.errors;
    }

    return result.data[`insert_${prefix}`].returning;
  }
  async function upsertMany(
    query: DocumentNode,
    params: any,
    form: any[]
  ): Promise<M[]> {
    const result = await gqlClient.mutate({
      mutation: query,
      variables: {
        form,
        ...params
      }
    });
    if (result.errors) {
      throw result.errors;
    }

    return result.data[`insert_${prefix}`].returning;
  }

  return {
    filter,
    getById,
    update,
    create,
    deleteById,
    fetchMany,
    createMany,
    upsert,
    upsertMany
  };

}
